<template>
  <div :id="'view_manager_' + props.entityType">
    <div class="flex items-center proxima_nova text_color">
      <div class="relative flex-grow min-w-0">
        <div
          ref="activeViewsContainer"
          class="overflow-x-auto overflow-y-hidden custom-scrollbar"
        >
          <VueDraggableNext
            :model-value="props.views.active"
            @update:model-value="updateActiveViews"
            item-key="id"
            :animation="200"
            ghost-class="ghost"
            @start="onDragStart"
            @end="onDragEnd"
            :class="['flex', 'flex-row']"
            :group="{ name: 'views', pull: false, put: false }"
          >
            <div
              v-for="(element, index) in props.views.active"
              :key="'active_view' + element.id + index"
              class="rounded-t-lg flex-shrink-0"
              :data-view-id="element.id"
            >
              <div
                v-if="
                  props.views.current && element.id === props.views.current.id
                "
                class="px-3 pb-2 pt-1 rounded-t-lg selected_view relative cursor-pointer items-center"
              >
                <div class="flex flex-row gap-2 items-center justify-center">
                  <span class="text-sm font-semibold flex items-center gap-1">
                    {{ element.name }}
                  </span>
                  <CustomDropdown
                    :disabled="
                      props.views.current &&
                      props.views.current.can_edit === false
                    "
                    ref="dropdownRef"
                    gap="0"
                    position="start"
                    dropdownCSS="p-0"
                    overflow-y="visible"
                    @close="handleDropdownClose"
                    :relativeToViewport="true"
                  >
                    <template #trigger>
                      <div
                        class="p-[5px] mt-1 icon_hover_effect"
                        :class="{ more_option_bg: isDropdownOpen }"
                        @click="handleEditDropdownOpen"
                      >
                        <ThemeImage
                          class="size-4"
                          :icon="imageNames.HORIZONTAL_THREE_DOTS_GREY"
                          alt="deals_icon"
                        />
                      </div>
                    </template>
                    <template #default>
                      <AddViewDropdownContent
                        ref="editViewContentRef"
                        mode="edit"
                        :current-view="props.views.current"
                        :view-name="props.views.current.name"
                        :access-level="
                          getAccessLevelFromView(props.views.current)
                        "
                        :access-options-by-tab="accessOptionsByTab"
                        :selected-members="
                          getSelectedMembersFromView(props.views.current)
                        "
                        :sharing-details="currentViewSharingDetails"
                        :enable-view-type="props.enableViewType"
                        :entity-slug="props.entityType"
                        @update-view="handleEditView"
                        @edit-view="handleEditViewClick"
                        @delete-view="handleDeleteViewClick"
                        @close-dropdown="handleDropdownClose"
                      />
                    </template>
                  </CustomDropdown>
                  <ThemeImage
                    v-if="props.views.active.length > 1"
                    :icon="imageNames.CLOSE_GREY"
                    alt="delete_icon"
                    class="size-4"
                    @click="handleCloseView(element)"
                  />
                </div>
              </div>
              <div
                v-else
                class="p-3 cursor-pointer max-w-[154px] overflow-hidden"
                @click="handleSetCurrentView(element.id)"
              >
                <span
                  class="text_color text-sm truncate flex items-center gap-1"
                >
                  {{ element.name }}
                </span>
              </div>
            </div>
          </VueDraggableNext>
        </div>
        <!-- Left fade gradient -->
        <div v-if="showLeftFade" class="scroll-fade-left"></div>
        <!-- Right fade gradient -->
        <div v-if="showRightFade" class="scroll-fade-right"></div>
      </div>

      <div v-if="props.views.current?.id" class="relative flex-shrink-0">
        <CustomDropdown
          ref="addViewDropdownRef"
          gap="4px"
          position="start"
          dropdownCSS="p-0"
          overflow-y="visible"
          @close="handleAddViewDropdownClose"
        >
          <template #trigger>
            <div
              class="icon_hover_effect flex cursor-pointer p-2 ml-2"
              :class="{ more_option_bg: isAddViewDropdownOpen }"
              @click="handleAddViewButton"
            >
              <ThemeImage
                :icon="imageNames.ADD_ROUNDED_GREY"
                alt="add_icon"
                class="size-4"
              />
            </div>
          </template>
          <template #default>
            <InactiveViewsDropdownContent
              v-if="showInActiveViewList && props.views.inactive?.length > 0"
              :inactive-views="props.views.inactive"
              @activate-view="handleActivateInactiveView"
              @add-new-view="handleAddNewView"
            />

            <AddViewDropdownContent
              v-else-if="showAddViewContent"
              ref="addViewContentRef"
              mode="create"
              :view-name="newViewName"
              :access-level="newViewAccessLevel"
              :access-options-by-tab="accessOptionsByTab"
              :selected-members="selectedMembers"
              :enable-view-type="props.enableViewType"
              :entity-slug="props.entityType"
              @update:viewName="(v) => (newViewName = v)"
              @update:accessLevel="(v) => (newViewAccessLevel = v)"
              @update:selectedMembers="(v) => (selectedMembers = v)"
              @delete-view="openDeleteViewModal"
              @save-view="handleCreateNewView"
              @view-type-selected="handleViewTypeSelected"
              @close-dropdown="handleAddViewDropdownClose"
            />
          </template>
        </CustomDropdown>
      </div>
    </div>

    <ConfirmationModal
      v-model:visible="showDeleteViewModal"
      title="Delete View"
      :message="`Are you sure you want to delete ${props.views.current?.name} view?`"
      type="delete"
      confirmText="Delete"
      cancelText="Cancel"
      :loading="isDeleting"
      loadingText="Deleting..."
      @confirm="handleDeleteView"
      @cancel="showDeleteViewModal = false"
    />
  </div>
</template>

<script setup>
import { VueDraggableNext } from "vue-draggable-next";
import { apiVariants } from "@/src/constants/appConstants.js";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { featureNames } from "@/src/constants/globalConstants";
import {
  entityTypes,
  viewEntityMapping,
} from "@/src/constants/entityTypeConstants.js";
import {
  accessLevels,
  sharingScopes,
  viewTypes,
} from "@/src/constants/viewConstants";
import eventBus from "@/src/utils/eventBus.js";
import {
  nextTick,
  onMounted,
  onUpdated,
  onBeforeUnmount,
  ref,
  watch,
  computed,
} from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";

import ViewsServiceV2 from "@/src/api/ViewsServiceV2.js";
import OutreachService from "@/src/api/OutreachService.js";
import DashboardService from "@/src/api/DashboardService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import AddViewDropdownContent from "@/src/components/view_components/AddViewDropdownContent.vue";
import InactiveViewsDropdownContent from "@/src/components/view_components/InactiveViewsDropdownContent.vue";

const props = defineProps({
  views: { type: Object, required: true },
  entityType: { type: String, required: true },
  apiVariant: { type: String, default: apiVariants.DASHBOARD },
  apiVersion: { type: String, default: "v1" },
  entityId: { type: [Number, String], default: null },
  sequenceId: { type: Number, default: null },
  enableViewType: { type: Boolean, default: false },
  localViewOnly: { type: Boolean, default: false },
  service: { type: String, default: null },
});

const emit = defineEmits([
  "update:views",
  "fetchView",
  "onNewViewCreated",
  "view-state-changed",
  "trigger-edit-view",
]);

const showDeleteViewModal = ref(false);
const isDeleting = ref(false);
const activeViewsContainer = ref(null);
const isDropdownOpen = ref(false);
const isAddViewDropdownOpen = ref(false);
const dropdownRef = ref(null);
const addViewDropdownRef = ref(null);
const addViewContentRef = ref(null);
const editViewContentRef = ref(null);

const showInActiveViewList = ref(false);
const showAddViewContent = ref(false);

const newViewName = ref("");
const newViewAccessLevel = ref(accessLevels.EVERYONE);
const selectedMembers = ref([]);
const viewSaveInProgress = ref(false);
const isDeleteActionTriggered = ref(false);

const accessOptionsByTab = ref({ Users: [], Teams: [] });
const currentViewSharingDetails = ref(null);
const showLeftFade = ref(false);
const showRightFade = ref(false);

const store = useStore();
const currentView = computed(() => props.views.current);

const getArrayRefValue = (refValue) =>
  Array.isArray(refValue) ? refValue[0] : refValue;

const getApiService = () => {
  if (props.apiVersion === "v2") {
    return ViewsServiceV2;
  }

  return props.apiVariant === apiVariants.DASHBOARD
    ? DashboardService
    : OutreachService;
};

const buildPayload = (basePayload) => {
  const payload = { ...basePayload };

  if (props.entityId) {
    payload.entity_id = props.entityId;
  }

  if (props.entityType === entityTypes.SEQUENCE_ENTITY && props.sequenceId) {
    payload.sequence_id = props.sequenceId;
  }

  return payload;
};

const resetFormState = () => {
  newViewName.value = "";
  newViewAccessLevel.value = accessLevels.EVERYONE;
  selectedMembers.value = [];
  viewSaveInProgress.value = false;
};

const closeDropdownStates = () => {
  showInActiveViewList.value = false;
  showAddViewContent.value = false;
};

const getAccessLevelFromView = (view) => {
  if (!view) return accessLevels.EVERYONE;

  const scopeMap = {
    workspace: accessLevels.EVERYONE,
    team_and_users: accessLevels.TEAM,
  };

  return scopeMap[view.sharing_scope] || accessLevels.PRIVATE;
};

const getSelectedMembersFromView = (view) => {
  if (!view) return [];

  if (currentViewSharingDetails.value?.sharing_info) {
    const { user_ids = [], team_ids = [] } =
      currentViewSharingDetails.value.sharing_info;
    return [...user_ids, ...team_ids];
  }

  const { user_ids = [], team_ids = [] } = view;
  return [...user_ids, ...team_ids];
};

const fetchViewSharingDetails = async (viewId) => {
  try {
    const service = getApiService();
    const data = await service.fetchViewSharingDetails(viewId);

    if (data?.success) {
      currentViewSharingDetails.value = data;
      return data;
    }
  } catch (error) {
    console.error("Failed to fetch view sharing details:", error);
  }
  return null;
};

const fetchUsersAndTeams = async () => {
  try {
    const [usersResp, teamsResp] = await Promise.all([
      DashboardService.fetchUsers("active"),
      DashboardService.fetchTeams(),
    ]);

    const userOptions = usersResp?.success
      ? (usersResp.users || []).map((u) => ({
          label: u.name || u.email,
          value: u.id || u.user_id,
        }))
      : [];

    const teamOptions = teamsResp?.success
      ? (teamsResp.teams || []).map((t) => ({
          label: t.name,
          value: t.id,
          members_count: t.users?.length || 0,
        }))
      : [];

    accessOptionsByTab.value = { Users: userOptions, Teams: teamOptions };
  } catch (error) {
    console.error("Failed to fetch users/teams for view access:", error);
    accessOptionsByTab.value = { Users: [], Teams: [] };
  }
};

const updateActiveViews = (newValue) => {
  emit("update:views", { ...props.views, active: newValue });
};

const handleEditDropdownOpen = async () => {
  isDeleteActionTriggered.value = false;
  currentViewSharingDetails.value = null;
  if (currentView.value) {
    await fetchViewSharingDetails(currentView.value.id);
  }
};

const handleDropdownClose = () => {
  if (isDeleteActionTriggered.value) {
    isDeleteActionTriggered.value = false;
    return;
  }

  const editComponent = getArrayRefValue(editViewContentRef.value);

  if (editComponent && editComponent.$props?.enableViewType) {
    return;
  }

  if (editComponent && typeof editComponent.saveView === "function") {
    editComponent.saveView();
    return;
  }
};

const handleAddViewButton = (event) => {
  event.stopPropagation();

  if (showInActiveViewList.value) {
    closeDropdownStates();
    addViewDropdownRef.value?.closeDropdown();
  } else if (props.views.inactive?.length > 0) {
    showInActiveViewList.value = true;
    showAddViewContent.value = false;
    nextTick(() => addViewDropdownRef.value?.toggleDropdown());
  } else {
    showAddViewContent.value = true;
    showInActiveViewList.value = false;
    nextTick(() => addViewDropdownRef.value?.toggleDropdown());
  }
};

const handleAddNewView = () => {
  showInActiveViewList.value = false;
  showAddViewContent.value = true;
};

const handleAddViewDropdownClose = () => {
  const addComponent = getArrayRefValue(addViewContentRef.value);

  if (showAddViewContent.value && !viewSaveInProgress.value && addComponent) {
    addComponent.saveView();
    return;
  }

  closeDropdownStates();
  resetFormState();
};

const handleEditView = async (updateData) => {
  if (!currentView.value) return;

  try {
    const payload = {
      view: {
        name: updateData.name,
        sharing_scope: updateData.sharing_scope,
        view_type: updateData.view_type,
        group_by_config: updateData.group_by_config,
      },
      team_ids: updateData.team_ids,
      user_ids: updateData.user_ids,
    };

    const service = getApiService();
    const data = await service.updateView(currentView.value.id, payload);

    if (data?.success) {
      const updatedView = data.view;

      const updatedActiveViews = props.views.active.map((view) => {
        if (view.id === currentView.value.id) {
          return { ...view, ...updatedView };
        }
        return view;
      });

      emit("update:views", {
        ...props.views,
        active: updatedActiveViews,
        current: updatedView,
      });

      if (!props.localViewOnly) {
        store.dispatch("views/setCurrentView", updatedView);
      }

      message.success("View updated successfully");
      emit("fetchView", { currentView: updatedView });

      const dropdownElement = getArrayRefValue(dropdownRef.value);
      dropdownElement?.closeDropdown();
    } else {
      message.error("Failed to update View");
    }
  } catch (error) {
    console.error("Failed to update the view:", error);
    message.error("Failed to update View");
  }
};

const handleCreateNewView = async (saveData) => {
  if (viewSaveInProgress.value) return;

  try {
    viewSaveInProgress.value = true;

    const service = getApiService();

    let payload;
    if (props.apiVersion === "v2") {
      const entityType =
        props.entityType === entityTypes.TASKS
          ? viewEntityMapping[entityTypes.PROJECTS]
          : props.entityType;
      payload = {
        name: saveData.name || "Untitled View",
        entity_type: entityType,
        filters: {},
        sort: [],
        sharing_scope: saveData.sharing_scope,
        team_ids: saveData.team_ids,
        user_ids: saveData.user_ids,
        view_type: saveData.view_type || "table",
        group_by_config: saveData.group_by_config || null,
      };

      if (props.entityId) {
        payload.entity_id = props.entityId;
      }
    } else {
      payload = buildPayload({
        entity_type: props.entityType,
        sharing_scope: saveData.sharing_scope,
        team_ids: saveData.team_ids,
        user_ids: saveData.user_ids,
      });
    }

    const data = await service.createNewView(payload);

    if (data?.success) {
      const newView = data.view;

      if (
        props.apiVersion === "v1" &&
        saveData.name &&
        saveData.name !== "Untitled View"
      ) {
        const updateData = await service.updateView(newView.id, {
          view: { name: saveData.name },
        });

        if (updateData?.success) {
          newView.name = saveData.name;
        }
      }

      const updatedActiveViews = [
        ...props.views.active,
        {
          id: newView.id,
          name: newView.name,
        },
      ];

      emit("update:views", {
        ...props.views,
        active: updatedActiveViews,
        current: newView,
      });

      if (!props.localViewOnly) {
        store.dispatch("views/setCurrentView", newView);
      }

      emit("onNewViewCreated", data.view.filters);

      resetFormState();
      message.success("View created successfully");

      addViewDropdownRef.value?.closeDropdown();
      await nextTick(() => scrollCurrentViewIntoView());
    } else {
      message.error("Failed to create View");
    }
  } catch (error) {
    console.error("Failed to create the view:", error);
    message.error("Failed to create View");
  } finally {
    viewSaveInProgress.value = false;
  }
};

const handleActivateInactiveView = async (viewId) => {
  try {
    const viewToActivate = props.views.inactive.find((v) => v.id === viewId);

    if (!viewToActivate) {
      message.error("View not found");
      return;
    }

    let viewData = viewToActivate;

    if (!props.localViewOnly) {
      const service = getApiService();
      const data = await service.updateCurrentView(viewId);

      const responseData = data?.data || data;
      viewData = responseData?.view || viewToActivate;

      if (!responseData?.success) {
        message.error("Failed to activate view");
        return;
      }
    }

    const updatedInactive = props.views.inactive.filter((v) => v.id !== viewId);
    const updatedActive = [...props.views.active, viewToActivate];

    emit("update:views", {
      ...props.views,
      active: updatedActive,
      inactive: updatedInactive,
      current: viewData,
    });

    if (!props.localViewOnly) {
      store.dispatch("views/setCurrentView", viewData);
    }

    emit("view-state-changed");
    emit("fetchView", { currentView: viewData });
    showInActiveViewList.value = false;
    addViewDropdownRef.value?.closeDropdown();
  } catch (error) {
    console.error("Failed to activate view:", error);
    message.error("An error occurred while activating the view");
  }
};

const handleSetCurrentView = async (viewId) => {
  try {
    const viewToSet = props.views.active.find((v) => v.id === viewId);

    if (!viewToSet) {
      console.error("View not found");
      return;
    }

    // Optimistic UI-state change only (no records fetch yet): the cached view
    // doesn't carry the persisted page_size/current_page, so fetching now
    // would send defaults and race with the fresh API response below.
    emit("view-state-changed", {
      viewId,
      startRecordsFetch: false,
      cachedViewType: viewToSet.view_type,
    });

    let viewData = viewToSet;

    if (!props.localViewOnly) {
      const service = getApiService();
      const data = await service.updateCurrentView(viewId);

      const responseData = data?.data || data;
      viewData = responseData?.view || viewToSet;

      if (!responseData?.success) {
        message.error("Failed to switch view");
        // Emit fetchView with current view to reset loading state
        emit("fetchView", { currentView: viewToSet, failed: true });
        return;
      }

      store.dispatch("views/setCurrentView", viewData);
    }

    // Fresh view now has persisted page_size/current_page — let the parent
    // run applyCurrentViewData and fetch with the correct pagination.
    emit("fetchView", {
      currentView: viewData,
      skipRecordsFetch: false,
    });
  } catch (error) {
    console.error("Failed to set current view:", error);
    message.error("Failed to switch view");
    // Emit fetchView to reset loading state on error
    const viewToSet = props.views.active.find((v) => v.id === viewId);
    emit("fetchView", {
      currentView: viewToSet || props.views.current,
      failed: true,
    });
  }
};

const handleCloseView = async (viewData) => {
  if (props.views.active.length <= 1) return;

  try {
    const service = getApiService();
    const data = await service.closeView(viewData.id);

    if (data?.success) {
      emit("fetchView");
    }
  } catch (error) {
    console.error("Failed to close view:", error);
  }
};

const handleDeleteViewClick = () => {
  isDeleteActionTriggered.value = true;
  openDeleteViewModal();
};

const handleViewTypeSelected = () => {
  addViewDropdownRef.value?.closeDropdown();
  closeDropdownStates();
  resetFormState();
};

const handleEditViewClick = () => {
  const dropdown = getArrayRefValue(dropdownRef.value);
  if (dropdown && typeof dropdown.closeDropdown === "function") {
    dropdown.closeDropdown();
  }

  closeDropdownStates();

  eventBus.emit("open-view-edit-mode");
};

const openDeleteViewModal = () => {
  showDeleteViewModal.value = true;
};

const handleDeleteView = async () => {
  if (!currentView.value) return;

  isDeleting.value = true;

  try {
    const service = getApiService();
    const data = await service.deleteView(currentView.value.id, props.service);

    if (data?.success) {
      emit("view-state-changed");
      emit("fetchView");
      showDeleteViewModal.value = false;
    } else {
      message.error("Failed to Delete View");
    }
  } catch (error) {
    console.error("Failed to delete the view:", error);
    message.error("Failed to Delete View");
  } finally {
    isDeleting.value = false;
  }
};

const onDragStart = (data) => {
  const draggedViewId = data.item._underlying_vm_.id;
  if (currentView.value && draggedViewId !== currentView.value.id) {
    handleSetCurrentView(draggedViewId);
  }
};

const onDragEnd = () => {
  const updatedSequence = props.views.active.map((view) => view.id);
  updateViewSequence(updatedSequence);
};

const updateViewSequence = async (updatedSequence) => {
  try {
    const service = getApiService();

    const payload =
      props.apiVersion === "v2"
        ? {
            entity_type: props.entityType,
            active_view_ids: updatedSequence,
          }
        : buildPayload({
            entity: props.entityType,
            activeViewIds: updatedSequence,
          });

    const response = await service.updateViewSequence(payload);

    if (!response?.success) {
      message.error(
        response?.errors || `Failed to update view ${featureNames.SEQUENCES}`,
      );
    }
  } catch (error) {
    console.error(`Error updating view ${featureNames.SEQUENCES}:`, error);
    message.error(
      `An error occurred while updating view ${featureNames.SEQUENCES}`,
    );
  }
};

const scrollCurrentViewIntoView = (callback) => {
  if (activeViewsContainer.value && currentView.value) {
    const currentViewElement = activeViewsContainer.value.querySelector(
      `[data-view-id="${currentView.value.id}"]`,
    );

    if (currentViewElement) {
      currentViewElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });

      if (callback) {
        setTimeout(callback, 300);
      }
    }
  }
};

const updateContainerWidth = () => {
  try {
    if (!activeViewsContainer.value) return;

    const parentElement = activeViewsContainer.value.closest("#action_bar");
    const parentWidth = parentElement?.offsetWidth;
    const actionButtonsWidth = parentElement?.querySelector(
      ".action_bar_buttons",
    )?.offsetWidth;
    const addViewButtonWidth =
      activeViewsContainer.value.nextElementSibling?.nextElementSibling
        ?.offsetWidth;

    if (parentWidth && actionButtonsWidth && addViewButtonWidth) {
      const availableWidth =
        parentWidth - actionButtonsWidth - addViewButtonWidth - 140;
      activeViewsContainer.value.style.maxWidth = `${availableWidth}px`;
    }
  } catch (error) {
    console.error("Error updating container width:", error);
  }
};

const updateScrollFades = () => {
  if (!activeViewsContainer.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = activeViewsContainer.value;

  showLeftFade.value = scrollLeft > 0;

  showRightFade.value = scrollLeft < scrollWidth - clientWidth - 1;
};

const handleScroll = () => {
  updateScrollFades();
};

watch(
  () => props.views.current,
  (newView, oldView) => {
    if (newView && oldView && newView.id !== oldView.id) {
      nextTick(() => scrollCurrentViewIntoView());
    }
  },
);

watch(showDeleteViewModal, (isOpen) => {
  if (!isOpen) {
    isDeleteActionTriggered.value = false;
  }
});

watch(
  () => props.views.active,
  () => {
    nextTick(() => updateScrollFades());
  },
  { deep: true },
);

watch(
  () => {
    const dropdown = getArrayRefValue(dropdownRef.value);
    return dropdown?.isOpen;
  },
  (newValue) => {
    if (newValue !== undefined) {
      isDropdownOpen.value = newValue;
    }
  },
);

watch(
  () => {
    const dropdown = getArrayRefValue(addViewDropdownRef.value);
    return dropdown?.isOpen;
  },
  (newValue) => {
    if (newValue !== undefined) {
      isAddViewDropdownOpen.value = newValue;
    }
  },
);

onMounted(() => {
  scrollCurrentViewIntoView();
  updateContainerWidth();
  fetchUsersAndTeams();

  if (activeViewsContainer.value) {
    activeViewsContainer.value.addEventListener("scroll", handleScroll);
    nextTick(() => updateScrollFades());
  }
});

onUpdated(() => {
  updateContainerWidth();
  updateScrollFades();
});

onBeforeUnmount(() => {
  if (activeViewsContainer.value) {
    activeViewsContainer.value.removeEventListener("scroll", handleScroll);
  }
});

defineExpose({
  accessOptionsByTab,
  currentViewSharingDetails,
});
</script>

<style scoped>
.selected_view {
  background-color: var(--background-color);
  border: 1px solid var(--white-opacity-10);
  border-bottom: none;
  z-index: 1;
}

.horizontal_line {
  background-color: var(--white-opacity-8);
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.more_option_bg {
  background-color: var(--white-opacity-8);
}

.custom-scrollbar {
  max-width: 38vw !important;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.custom-scrollbar:hover {
  scrollbar-color: rgba(128, 128, 128, 0.3) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  height: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 10px;
  transition: background-color 0.2s ease;
}

.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: rgba(128, 128, 128, 0.3);
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(128, 128, 128, 0.5);
}

.scroll-fade-left {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 4px;
  background: linear-gradient(
    to right,
    var(--background-color) 0%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 2;
}

.scroll-fade-right {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 4px;
  background: linear-gradient(
    to left,
    var(--background-color) 0%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 2;
}
</style>
